import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'chart.js/auto';
import { Card, Form, Spinner } from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasAnyPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl } from '../../config/apiConfig';
import { formatARS } from '../../utils/currency';
import { logError } from '../../utils/logger';
import '../../styles/admin.css';
import './AdminReportsPage.css';

const PERIOD_OPTIONS = [
  { value: 'day', label: 'Últ. 24 h' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' },
];

const REPORT_ACCESS = [
  PERMISSION_IDS.PANEL_REPORTES,
  PERMISSION_IDS.VER_REPORTES,
  PERMISSION_IDS.PANEL_ADMIN,
];

const ORDER_STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'Procesando',
  paid: 'Pagado',
  completed: 'Completado',
  canceled: 'Cancelado',
  refunded: 'Reembolsado',
  failed: 'Fallido',
};

const truncateLabel = (text, max = 32) => {
  const s = String(text || '').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
};

const AdminReportsPageInner = () => {
  const { user, token } = useAdminGuard();
  const navigate = useNavigate();
  const { notify } = useAdminNotifications();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  const salesRef = useRef(null);
  const statusRef = useRef(null);
  const itemsRef = useRef(null);
  const coursesRef = useRef(null);
  const pagesRef = useRef(null);
  const charts = useRef({
    sales: null,
    status: null,
    items: null,
    courses: null,
    pages: null,
  });

  const canView = useMemo(
    () => hasAnyPermission(user, REPORT_ACCESS),
    [user],
  );

  const handleSidebarNavigate = useCallback(
    (item) => {
      if (item.path === '/admin' || item.path === '/admin/dashboard' || item.eventKey === 'dashboard') {
        navigate('/admin');
        return;
      }
      if (item.path) {
        navigate(item.path);
      }
    },
    [navigate],
  );

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const loadOverview = useCallback(async () => {
    if (!token || !canView) return;
    setLoading(true);
    try {
      const res = await fetch(
        buildApiUrl(`/api/admin/reports/overview?period=${encodeURIComponent(period)}&limit=8`),
        { headers: authHeaders },
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || res.statusText);
      }
      const data = await res.json();
      setPayload(data);
    } catch (err) {
      logError('[AdminReportsPage]', err);
      notify({
        title: 'Error al cargar reportes',
        message: err.message || 'No se pudo obtener la información.',
        variant: 'error',
      });
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, canView, notify, period, token]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(
    () => () => {
      Object.keys(charts.current).forEach((key) => {
        if (charts.current[key]) {
          charts.current[key].destroy();
          charts.current[key] = null;
        }
      });
    },
    [],
  );

  const salesSeries = payload?.salesSeries || [];
  const orderStatusMix = payload?.orderStatusMix || [];
  const topItems = payload?.topItems || [];
  const topCourses = payload?.topCourses || [];
  const pageStats = payload?.online?.pageStats || {};

  const sortedPages = useMemo(() => {
    return Object.entries(pageStats)
      .map(([page, count]) => ({ page, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [pageStats]);

  useEffect(() => {
    if (!salesRef.current) return undefined;
    if (!salesSeries.length) {
      if (charts.current.sales) {
        charts.current.sales.destroy();
        charts.current.sales = null;
      }
      return undefined;
    }
    const labels = salesSeries.map((r) => truncateLabel(r.label, 14));
    const revenues = salesSeries.map((r) => r.revenue);

    if (charts.current.sales) {
      charts.current.sales.data.labels = labels;
      charts.current.sales.data.datasets[0].data = revenues;
      charts.current.sales.update('active');
      return undefined;
    }

    charts.current.sales = new Chart(salesRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Ingresos (órdenes)',
            data: revenues,
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: 'rgba(99, 102, 241, 0.12)',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 550, easing: 'easeOutQuart' },
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            padding: 12,
            cornerRadius: 10,
            callbacks: {
              label(ctx) {
                const v = ctx.parsed.y;
                return ` ${formatARS(v)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 }, color: '#64748b' },
          },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(148, 163, 184, 0.2)' },
            ticks: {
              color: '#64748b',
              font: { size: 10 },
              callback(value) {
                const n = Number(value);
                if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
                if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
                return n;
              },
            },
          },
        },
      },
    });

    return () => {
      if (charts.current.sales) {
        charts.current.sales.destroy();
        charts.current.sales = null;
      }
    };
  }, [salesSeries]);

  useEffect(() => {
    if (!statusRef.current) return undefined;
    const filtered = orderStatusMix.filter((r) => r.count > 0);
    if (!filtered.length) {
      if (charts.current.status) {
        charts.current.status.destroy();
        charts.current.status = null;
      }
      return undefined;
    }
    const labels = filtered.map((r) => ORDER_STATUS_LABELS[r.status] || r.status);
    const data = filtered.map((r) => r.count);
    const colors = [
      'rgba(99, 102, 241, 0.85)',
      'rgba(34, 197, 94, 0.85)',
      'rgba(234, 179, 8, 0.88)',
      'rgba(248, 113, 113, 0.88)',
      'rgba(148, 163, 184, 0.85)',
      'rgba(14, 165, 233, 0.85)',
      'rgba(168, 85, 247, 0.85)',
    ];

    if (charts.current.status) {
      charts.current.status.data.labels = labels;
      charts.current.status.data.datasets[0].data = data;
      charts.current.status.data.datasets[0].backgroundColor = labels.map(
        (_, i) => colors[i % colors.length],
      );
      charts.current.status.update('active');
      return undefined;
    }

    charts.current.status = new Chart(statusRef.current, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 0,
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '58%',
        animation: { animateRotate: true, duration: 600 },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { boxWidth: 10, padding: 10, font: { size: 11 } },
          },
        },
      },
    });

    return () => {
      if (charts.current.status) {
        charts.current.status.destroy();
        charts.current.status = null;
      }
    };
  }, [orderStatusMix]);

  useEffect(() => {
    if (!itemsRef.current) return undefined;
    if (!topItems.length) {
      if (charts.current.items) {
        charts.current.items.destroy();
        charts.current.items = null;
      }
      return undefined;
    }
    const labels = topItems.map((item) => truncateLabel(item.name));
    const data = topItems.map((item) => item.totalQty);

    if (charts.current.items) {
      charts.current.items.data.labels = labels;
      charts.current.items.data.datasets[0].data = data;
      charts.current.items.update('active');
      return undefined;
    }

    charts.current.items = new Chart(itemsRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Unidades',
            data,
            borderRadius: 8,
            maxBarThickness: 26,
            backgroundColor: 'rgba(59, 130, 246, 0.78)',
            hoverBackgroundColor: 'rgba(37, 99, 235, 0.92)',
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            padding: 12,
            cornerRadius: 10,
            displayColors: false,
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: 'rgba(148, 163, 184, 0.22)', drawBorder: false },
            ticks: { font: { size: 11 }, color: '#64748b' },
          },
          y: {
            grid: { display: false, drawBorder: false },
            ticks: { font: { size: 11 }, color: '#334155' },
          },
        },
      },
    });

    return () => {
      if (charts.current.items) {
        charts.current.items.destroy();
        charts.current.items = null;
      }
    };
  }, [topItems]);

  useEffect(() => {
    if (!coursesRef.current) return undefined;
    if (!topCourses.length) {
      if (charts.current.courses) {
        charts.current.courses.destroy();
        charts.current.courses = null;
      }
      return undefined;
    }
    const labels = topCourses.map((c) => truncateLabel(c.name));
    const data = topCourses.map((c) => c.totalQty);

    if (charts.current.courses) {
      charts.current.courses.data.labels = labels;
      charts.current.courses.data.datasets[0].data = data;
      charts.current.courses.update('active');
      return undefined;
    }

    charts.current.courses = new Chart(coursesRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Ventas',
            data,
            borderRadius: 8,
            maxBarThickness: 26,
            backgroundColor: 'rgba(34, 197, 94, 0.78)',
            hoverBackgroundColor: 'rgba(22, 163, 74, 0.92)',
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            padding: 12,
            cornerRadius: 10,
            displayColors: false,
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: 'rgba(148, 163, 184, 0.22)', drawBorder: false },
            ticks: { font: { size: 11 }, color: '#64748b' },
          },
          y: {
            grid: { display: false, drawBorder: false },
            ticks: { font: { size: 11 }, color: '#334155' },
          },
        },
      },
    });

    return () => {
      if (charts.current.courses) {
        charts.current.courses.destroy();
        charts.current.courses = null;
      }
    };
  }, [topCourses]);

  useEffect(() => {
    if (!pagesRef.current) return undefined;
    if (!sortedPages.length) {
      if (charts.current.pages) {
        charts.current.pages.destroy();
        charts.current.pages = null;
      }
      return undefined;
    }
    const labels = sortedPages.map((p) => truncateLabel(p.page, 20));
    const data = sortedPages.map((p) => p.count);

    if (charts.current.pages) {
      charts.current.pages.data.labels = labels;
      charts.current.pages.data.datasets[0].data = data;
      charts.current.pages.update('active');
      return undefined;
    }

    charts.current.pages = new Chart(pagesRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Usuarios',
            data,
            borderRadius: 6,
            maxBarThickness: 22,
            backgroundColor: 'rgba(14, 165, 233, 0.75)',
            hoverBackgroundColor: 'rgba(2, 132, 199, 0.9)',
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { stepSize: 1, font: { size: 10 }, color: '#64748b' },
            grid: { color: 'rgba(148, 163, 184, 0.2)', drawBorder: false },
          },
          y: {
            grid: { display: false, drawBorder: false },
            ticks: { font: { size: 10 }, color: '#334155' },
          },
        },
      },
    });

    return () => {
      if (charts.current.pages) {
        charts.current.pages.destroy();
        charts.current.pages = null;
      }
    };
  }, [sortedPages]);

  if (!canView) {
    return (
      <div className="admin-page">
        <AdminSidebar activeKey="reports" onNavigate={handleSidebarNavigate} />
        <div className="admin-content">
          <Card className="AR-denied shadow-sm">
            <Card.Body className="p-4">
              <h2 className="h5 mb-2">Sin acceso a Reportes</h2>
              <p className="text-muted mb-0">
                Necesitas el permiso <strong>PanelReportes</strong>, <strong>VerReportes</strong> o rol
                administrador completo. Pide a un administrador que asigne esos permisos a tu rol.
              </p>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  const ps = payload?.paymentStats;
  const nu = payload?.users;
  const nl = payload?.newsletter;
  const online = payload?.online;

  return (
    <div className="admin-page">
      <AdminSidebar activeKey="reports" onNavigate={handleSidebarNavigate} />
      <div className="admin-content admin-reports">
        <div className="AR-hero d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
          <div className="AR-hero-text">
            <h1 className="AR-hero-title">
              <i className="bi bi-graph-up-arrow" aria-hidden />
              Reportes
            </h1>
            <p className="AR-hero-sub">
              Vista consolidada de ventas, órdenes, audiencia en línea y newsletters. Los datos se
              actualizan al cambiar el período.
            </p>
          </div>
          <div className="AR-period">
            <Form.Label className="text-white-50 small mb-1 d-block">Período</Form.Label>
            <Form.Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              aria-label="Período del reporte"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        {loading && (
          <div className="d-flex align-items-center gap-2 text-muted py-4">
            <Spinner animation="border" size="sm" />
            Cargando métricas…
          </div>
        )}

        {!loading && payload && (
          <>
            <div className="AR-kpi-grid">
              <div className="AR-kpi">
                <div className="AR-kpi-label">Usuarios</div>
                <div className="AR-kpi-value">{nu?.totalUsers ?? '—'}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">Con compra</div>
                <div className="AR-kpi-value">{nu?.buyers ?? '—'}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">Ingresos (transacciones OK)</div>
                <div className="AR-kpi-value">{formatARS(ps?.totalRevenue)}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">Transacciones completadas</div>
                <div className="AR-kpi-value">{ps?.completed ?? '—'}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">Ingresos último mes (MP)</div>
                <div className="AR-kpi-value">{formatARS(ps?.monthly?.revenue)}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">En línea ahora</div>
                <div className="AR-kpi-value">{online?.totalOnline ?? 0}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">Newsletters activos</div>
                <div className="AR-kpi-value">{nl?.active ?? '—'}</div>
              </div>
              <div className="AR-kpi">
                <div className="AR-kpi-label">Vistas newsletter</div>
                <div className="AR-kpi-value">{nl?.views ?? '—'}</div>
              </div>
            </div>

            <section className="AR-chart-section" aria-labelledby="ar-section-ventas">
              <h2 id="ar-section-ventas" className="AR-section-title">
                Ventas y órdenes
              </h2>
              <div className="AR-charts-row AR-charts-row--cols-2">
                <div className="AR-chart-card AR-chart-card--balanced">
                  <div className="AR-chart-card-header">
                    <h3>
                      <i className="bi bi-activity" aria-hidden />
                      Ingresos por órdenes pagadas / completadas
                    </h3>
                  </div>
                  <div className="AR-chart-body">
                    {!salesSeries.length ? (
                      <div className="AR-chart-placeholder">
                        <i className="bi bi-inbox fs-2 d-block mb-2 opacity-50" aria-hidden />
                        Sin datos en este período
                      </div>
                    ) : (
                      <div className="AR-chart-canvas-wrap">
                        <canvas ref={salesRef} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="AR-chart-card AR-chart-card--balanced">
                  <div className="AR-chart-card-header">
                    <h3>
                      <i className="bi bi-pie-chart" aria-hidden />
                      Órdenes por estado
                    </h3>
                  </div>
                  <div className="AR-chart-body">
                    {!orderStatusMix.filter((r) => r.count > 0).length ? (
                      <div className="AR-chart-placeholder">Sin órdenes en el rango</div>
                    ) : (
                      <div className="AR-chart-canvas-wrap AR-chart-canvas-wrap--donut">
                        <canvas ref={statusRef} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="AR-chart-section" aria-labelledby="ar-section-catalogo">
              <h2 id="ar-section-catalogo" className="AR-section-title">
                Catálogo vendido
              </h2>
              <div className="AR-charts-row AR-charts-row--cols-2">
                <div className="AR-chart-card AR-chart-card--balanced">
                  <div className="AR-chart-card-header">
                    <h3>
                      <i className="bi bi-box-seam" aria-hidden />
                      Top ítems físicos / tienda
                    </h3>
                  </div>
                  <div className="AR-chart-body">
                    {!topItems.length ? (
                      <div className="AR-chart-placeholder">Sin ventas de ítems en el período</div>
                    ) : (
                      <div className="AR-chart-canvas-wrap">
                        <canvas ref={itemsRef} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="AR-chart-card AR-chart-card--balanced">
                  <div className="AR-chart-card-header">
                    <h3>
                      <i className="bi bi-mortarboard" aria-hidden />
                      Top cursos vendidos
                    </h3>
                  </div>
                  <div className="AR-chart-body">
                    {!topCourses.length ? (
                      <div className="AR-chart-placeholder">Sin ventas de cursos en el período</div>
                    ) : (
                      <div className="AR-chart-canvas-wrap">
                        <canvas ref={coursesRef} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="AR-chart-section" aria-labelledby="ar-section-uso">
              <h2 id="ar-section-uso" className="AR-section-title">
                Uso en vivo
              </h2>
              <div className="AR-charts-row">
                <div className="AR-chart-card AR-chart-card--wide">
                  <div className="AR-chart-card-header">
                    <h3>
                      <i className="bi bi-people" aria-hidden />
                      Páginas con más usuarios en línea (últimos 5 min)
                    </h3>
                  </div>
                  <div className="AR-chart-body">
                    {!sortedPages.length ? (
                      <div className="AR-chart-placeholder">
                        Nadie con sesión activa en este momento
                      </div>
                    ) : (
                      <div className="AR-chart-canvas-wrap AR-chart-canvas-wrap--wide">
                        <canvas ref={pagesRef} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

const AdminReportsPage = () => (
  <AdminNotificationsProvider>
    <AdminReportsPageInner />
  </AdminNotificationsProvider>
);

export default AdminReportsPage;
